/**
 * useChatBridge — composes the backend ``/api/chat-stream`` endpoint
 * into Variant C's EventLog model.
 *
 * The rail sends non-slash text here; the hook fires the backend
 * request, tracks the streaming indicator, and emits one
 * ``askwhy`` audit event per completed Q&A round.
 *
 * SRP: owns the bridge between raw chat requests and the
 * reducer-dispatched audit trail. Does NOT own rendering, path
 * resolution, command parsing, or scope resolution — those live in
 * the rail / shell / cursorScope.ts respectively.
 *
 * # DECISION (2026-04-22, superseded 2026-04-23): emit a single
 * #   ``askwhy`` event on ``complete``, NOT a separate ``prompt``
 * #   on send + ``askwhy`` on complete.
 * # WHY (original): Q&A is the natural audit unit.
 * # SUPERSEDED (2026-04-23): Spec 29 promotes Timeline to be the
 * #   primary chat surface. Writer's message MUST land in the
 * #   timeline immediately on send — waiting 5–30s for the askwhy
 * #   to arrive would break the "chat IS the timeline" promise.
 * #   We now emit ``prompt`` on send (renders as a writer row) AND
 * #   ``askwhy`` on complete (carries the paired Q&A audit record).
 * #   Rendering overlap is avoided by showing the answer preview
 * #   (not the question) in the askwhy row's title — see
 * #   TimelineView.formatEventTitle.
 *
 * # DECISION: v1 gates chat to post-run only. Mid-run chat is on
 * #   the roadmap but requires a backend tool-filter first (see
 * #   docs/specs/27-variant-c-freeform-chat.md §Mid-run).
 * # WHY: per user's Q1, mid-run chat should be read-only — i.e.
 * #      ``doc_write_section`` filtered out of the tool set while
 * #      a run is live. Without that backend filter, mid-run chat
 * #      gives the agent write access while Asthra's drafting
 * #      loop is also writing — a race. Shipping post-run only
 * #      avoids the hack; the gate flips when Spec 27's backend
 * #      delta lands.
 * # DATE: 2026-04-22
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chatWithDocumentStream, streamAbbreviations } from "../../api/client";
import { buildLiveStatusPhrase, describeToolCall, inputFromQuery } from "../../config/toolDisplayNames";
import type { BridgeStatus } from "../../hooks/useUserInputBridge";
import type { ResolvedScope } from "../cursorScope";
import type { AppendInput } from "../eventLog";
import { formatSectionLabelFromNumber } from "../sectionLabel";
import { parseChatChanges } from "../services/chatChanges";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Target-resolution passed in per-call from the shell. The bridge
 *  is agnostic about how the shell turns a ``ResolvedScope`` into
 *  these ids — that's the shell's knowledge of its own section list. */
export interface ChatDispatchTarget {
  /** Backend ``section_number`` argument ("9.2"). Undefined when
   *  the scope is document-wide. */
  sectionNumber: string | undefined;
  /** Internal EventLog ``sectionId`` (module id, e.g. "CON-401").
   *  Null when the event is run-wide. */
  sectionId: string | null;
  /** Text the writer had highlighted in Word at the moment they
   *  submitted the message. Forwarded to the backend so the agent
   *  sees the selection as explicit context without the writer
   *  needing to paste it. Undefined when nothing is selected. */
  selectedText?: string;
  /** Snapshot of the message the writer is replying to. Embedded in
   *  the EventLog entry and forwarded to the agent as context. */
  quotedMessage?: import("../messageProjection").QuotedMessage;
  /** "research" — intent-routed research on the highlighted text.
   *  Backend prioritizes search tools and drops write tools. */
  mode?: "default" | "research";
  /** Intent classified at submit (routing on unless the
   *  chat_intent_routing_disabled kill switch is set).
   *  Stamped onto the prompt EventLog entry (durable chip) and held
   *  as ``streamingIntent`` while the response streams so the status
   *  line can carry the label end-to-end. Undefined when routing is
   *  off or classification was skipped. */
  intent?: import("../../api/client").ChatRoutedIntent;
  /** Writer-attached source assets (@-mentions). Forwarded to the
   *  backend, which resolves them to content before the tool loop. */
  attachments?: import("../../types").MentionAttachment[];
}

export interface ChatBridgeParams {
  /** Resolved Word document path. Post-run, the shell populates
   *  it from ``getRunStatus(runId).docx_path``. When null (idle /
   *  pre-run / mid-run), chat still proceeds AS LONG AS
   *  ``connectionId`` is set — the backend uses the live
   *  Office.js bridge to read/write doc content, so no on-disk
   *  path is required.
   *
   *  # DECISION: chat works pre-run via live bridge; docxPath is
   *  #   a convenience, not a hard requirement.
   *  # WHY: writer opens a pre-existing Word doc, wants to ask
   *  #      "fix §9.1's grammar." Backend's chat-stream endpoint
   *  #      accepts empty docx_path when connection_id resolves a
   *  #      live bridge (server/routes/chat.py L313, L186 — see
   *  #      ops_override wiring). The prior frontend guard
   *  #      artificially gated this flow off with the misleading
   *  #      copy "Chat opens up once a run exists", which was a
   *  #      bug not a design.
   *  # DATE: 2026-04-24
   */
  docxPath: string | null;
  /** Run-identity fields passed to the backend so the chat agent's
   *  provenance queries are scoped to this run. */
  trial: string | undefined;
  session: string | undefined;
  docType: string | undefined;
  libraryId: string | undefined;
  runId: string | null;
  /** Restored conversation thread ID from the persisted DocumentSession.
   *  Seeded once on mount (or when it arrives after async session load)
   *  so that a writer who reopens a document resumes the same multi-turn
   *  thread rather than starting a new one.
   *
   *  Only applied when the hook's internal conversationId is still
   *  undefined — a live chat-initiated ID always takes precedence. */
  initialConversationId?: string;
  /** WebSocket connection id from ``useUserInputBridge``. Threaded
   *  into the backend so tools that need writer approval
   *  (``search_literature``, ``request_user_input``) can route their
   *  approval request through the bridge. Null when the bridge
   *  hasn't connected yet; approval-required tools will politely
   *  decline until it does.
   *
   *  Without this, the backend's ``_request_search_approval`` falls
   *  into non-interactive mode and auto-rejects every external
   *  search — writers see the agent say "literature search is
   *  restricted", which is accurate but unhelpful. */
  connectionId: string | null;
  /** Lifecycle status of the WebSocket bridge from
   *  ``useUserInputBridge``. Drives the queue-and-retry behaviour
   *  in ``sendChat``: when both ``docxPath`` and ``connectionId``
   *  are null but the bridge is still ``connecting``, the message
   *  is held and replayed on the next connect rather than rejected
   *  with a confusing "reopen the task pane" hint.
   *
   *  Defaults to ``"connected"`` so older callers that don't yet
   *  thread the status through retain the prior behaviour
   *  (proceed-or-error, no queueing). */
  bridgeStatus?: BridgeStatus;
  /** Opt into the v3 task-orchestrated chat agent. When true, the
   *  bridge routes to ``/api/chat-stream/v3`` and surfaces the
   *  planner's task list + progress updates in
   *  ``ChatBridgeState.tasks`` / ``completedTasks`` / etc. When
   *  false (the default), the legacy chat-stream endpoint is used
   *  and the v3-specific state stays empty. */
  useAgentV3?: boolean;
  /** v3 agent: hint for how to fan retrieval across the library.
   *  ``single_doc`` = one targeted call against the active doc,
   *  ``multi_doc_sequential`` = iterate the small library doc-by-doc,
   *  ``full_library`` = a single call against the whole library.
   *  Undefined lets the backend resolve from the active library
   *  size; explicit values override that auto-resolution. */
  libraryMode?: "single_doc" | "multi_doc_sequential" | "full_library";
  /** Called with an append input for every completed Q&A. Shell
   *  wires this to the reducer's ``EVENT_APPENDED`` action. Kept
   *  as a callback so the hook doesn't depend on the reducer. */
  onEvent: (entry: AppendInput) => void;
}

/** Task surfaced by the v3 chat agent's planner. Mirrors the
 *  backend's ``ChatTask`` Pydantic model (server/routes/chat_agent_v3.py).
 *  Emitted via the ``plan_ready`` SSE event and updated through
 *  ``task_start`` / ``task_done`` / ``task_failed``. */
export interface ChatTask {
  id: string;
  title: string;
  intent: string;
  status: "pending" | "in_progress" | "done" | "failed" | "skipped";
  failure_reason?: string | null;
}

/** The tool the agent is currently running, as surfaced by the SSE
 *  stream. Carries the input + retrieval surface so the rail status
 *  line can show context ("searching PubMed for …"), not just a verb.
 *  Null when no tool is active (think-time / between tools). */
export interface LiveTool {
  name: string;
  input?: Record<string, unknown>;
  surface?: string;
}

/** One in-flight tool step of the current chat turn — the transient
 *  "running" activity box. Durable history is written separately: when
 *  the step resolves, the bridge appends ONE ``chat_tool_step``
 *  EventLog entry (status done/error) and drops the transient step, so
 *  the append-only EventLog never holds a frozen spinner. */
export interface LiveToolStep {
  stepId: string;
  /** Backend tool id (or ``"task"`` for v3 planner tasks). */
  tool: string;
  input?: Record<string, unknown>;
  startedAt: number;
  /** Pre-built summary for v3 tasks (their title); tools derive their
   *  summary from ``input`` via the toolDisplayNames SSOT instead. */
  summaryOverride?: string;
  /** The tool's main argument, untruncated (stream ``tool_query`` or the
   *  input's ``document``) — carried onto the durable step. */
  toolQuery?: string;
}

/** Ref-backed store for the live steps — the SSE handler needs
 *  read-modify-write without side effects inside React state updaters
 *  (StrictMode runs updaters twice; an onEvent call in one would
 *  double-append durable entries). ``set`` updates the ref AND the
 *  mirrored render state. */
export interface LiveStepsStore {
  get: () => readonly LiveToolStep[];
  set: (steps: readonly LiveToolStep[]) => void;
}

export interface ChatBridgeState {
  /** True while a chat request is in flight. */
  isStreaming: boolean;
  /** The tool running during streaming (drives the rail's live status
   *  line), or null when no tool is active. */
  liveTool: LiveTool | null;
  /** In-flight tool steps of the current turn — rendered as transient
   *  "running" activity boxes in the timeline tail. Empty when idle
   *  (resolved steps live in the EventLog as ``chat_tool_step``). */
  liveSteps: readonly LiveToolStep[];
  /** Latest assistant reply text, for the rail's assistant-line
   *  render. Null when no chat yet / explicitly cleared. */
  lastReply: string | null;
  /** Text of a message held while the bridge is still
   *  ``connecting``. Null when nothing is queued. The rail renders
   *  this as a soft hint ("Sending once connection is ready…") so
   *  the writer knows the message wasn't dropped. Replayed
   *  automatically when ``connectionId`` resolves. */
  queuedMessage: string | null;
  /** v3 agent: list of tasks the planner emitted. Empty when the
   *  v3 agent isn't in use, or before ``plan_ready`` arrives. */
  tasks: ChatTask[];
  /** v3 agent: count of tasks resolved (done/failed/skipped). */
  completedTasks: number;
  /** v3 agent: total tasks in the current plan. */
  totalTasks: number;
  /** v3 agent: id of the task currently in progress, or null when
   *  no task is active. */
  currentTaskId: string | null;
  /** v3 agent: title of the task currently in progress (mirrors
   *  ``currentTaskId`` for display convenience). Null when idle. */
  currentTaskTitle: string | null;
  /** v3 agent: status message shown while Claude is composing the
   *  final response (after all tasks finish). Null otherwise. */
  draftingStatus: string | null;
  /** Setup/progress label from backend ``status`` SSE events
   *  ("Checking source library connection…", "Thinking…"). Fills the
   *  previously-silent setup window before the first tool fires.
   *  Null when idle or once a tool verb takes over. */
  statusLabel: string | null;
  /** Assistant text accumulated mid-stream from ``text`` SSE events —
   *  the "draft so far" bubble. Legacy chat replaces it per round; v3
   *  appends token deltas. Cleared on ``complete``/``error`` (the
   *  final reply lands via the ``askwhy`` timeline event). */
  streamingDraft: string | null;
  /** Epoch ms when the in-flight request started — drives the
   *  elapsed-seconds display. Null when not streaming. */
  streamStartedAt: number | null;
  /** Intent classified for the in-flight message (routing on unless
   *  chat_intent_routing_disabled). Held for the stream so the status line
   *  can prefix "[Label] Asthra is …" end-to-end; null when idle or
   *  routing is off. */
  streamingIntent: import("../../api/client").ChatRoutedIntent | null;
}

/**
 * Chat can proceed when EITHER:
 *   - ``docxPath`` is resolved (typical post-run case — the shell
 *     has a .docx path from ``getRunStatus``), OR
 *   - ``connectionId`` is set (live-bridge case — Office.js
 *     WebSocket is connected, so doc-tools read/write through it
 *     regardless of any on-disk file).
 *
 * Returns null when the precondition passes, or a writer-facing
 * error string when it fails.
 *
 * # DECISION (2026-04-24): extracted as a pure helper so we can
 * #   pin this contract with a test. The prior inline guard
 * #   required docxPath unconditionally, which blocked the
 * #   "chat with a pre-existing Word doc before any run" flow —
 * #   the exact bug a writer hit in the pane. The extraction
 * #   ensures future refactors can't silently re-introduce the
 * #   over-strict check.
 * # WHY: backend accepts empty docx_path when ops_override
 * #      (LiveDocumentSource) is set from a valid connection_id
 * #      (server/routes/chat.py L186, L313). Doc tools
 * #      (doc_read_section, doc_list_sections, doc_write_section)
 * #      read/write through the bridge regardless of path. The
 * #      path was a convenience, not a hard requirement.
 */
export function checkChatPreconditions(docxPath: string | null, connectionId: string | null): string | null {
  if (!docxPath && !connectionId) {
    return "Live document connection isn't ready yet — try reopening the task pane.";
  }
  return null;
}

/**
 * Classify *why* a chat send is currently blocked, given the bridge
 * lifecycle status. Only meaningful when ``checkChatPreconditions``
 * has already returned a non-null value (i.e. both ``docxPath`` and
 * ``connectionId`` are missing).
 *
 *   - "queue": the bridge is still dialing. The caller should hold
 *     the message and replay it once ``connectionId`` resolves.
 *     Writer-facing copy is informational ("Sending once
 *     connection is ready…"), not an error.
 *   - "error": the bridge dial finished and we're not connected.
 *     The caller should surface the error string verbatim — it
 *     names the actual failure mode (server unreachable) instead
 *     of the misleading "reopen the task pane" hint that the
 *     legacy precondition returned.
 */
export function classifyBridgeBlock(status: BridgeStatus): {
  kind: "queue" | "error";
  message: string;
} {
  if (status === "connecting") {
    return {
      kind: "queue",
      message: "Sending once the backend connection is ready…",
    };
  }
  // "disconnected" — and the defensive "connected-but-no-id" fallback,
  // which shouldn't be reachable but is handled here for completeness.
  return {
    kind: "error",
    message: "Backend bridge unreachable — make sure the server is running on port 8000, then reopen the task pane.",
  };
}

export interface ChatBridgeAPI extends ChatBridgeState {
  /** Send a message. Returns ``null`` on clean dispatch, or a
   *  writer-facing error string (surfaced in the rail's error-hint
   *  banner) when the send couldn't proceed — e.g. ``docxPath``
   *  hasn't resolved yet or the backend errored. */
  sendChat: (message: string, scope: ResolvedScope, target: ChatDispatchTarget) => Promise<string | null>;
  /** Clear the last-reply cached in the hook (e.g. when the writer
   *  resets the pane / starts a new run). */
  clearLastReply: () => void;
  /** Abort the in-flight chat request (writer clicked Stop in the
   *  composer). No-op when nothing is streaming. Aborting drops the
   *  fetch connection so the backend ``StreamingResponse`` cancels;
   *  the ``finally`` in ``performSend`` resets ``isStreaming`` and
   *  the composer button flips back to Send. */
  abortChat: () => void;
  /** Run the ``/abbreviations`` command — streams the server-side build
   *  through the same tool-step UI as chat, landing the finished table
   *  as an assistant bubble. Returns ``null`` on clean dispatch or a
   *  writer-facing error string. Requires a resolved ``runId``. */
  runAbbreviations: (runId: string) => Promise<string | null>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Writer-facing hint returned when a send is rejected because a
 *  previous chat request is still streaming. Surfaces through the
 *  rail's existing errorHint path (same contract as any other
 *  string return from ``sendChat``). */
export const CHAT_BUSY_MESSAGE = "The agent is still responding — wait for the current reply to finish.";

/** Substituted when a ``complete`` event carries an empty/whitespace
 *  message (reasoning-only model turn). Mirrors the server-side
 *  _EMPTY_REPLY_FALLBACK in server/routes/chat.py — the server should
 *  never send an empty message, but the projection dropping empty
 *  answers makes a silent dead-end costly enough to guard twice. */
export const EMPTY_REPLY_FALLBACK =
  "I wasn't able to compose a reply for that request — please try rephrasing or ask again.";

export function useChatBridge(params: ChatBridgeParams): ChatBridgeAPI {
  const [isStreaming, setIsStreaming] = useState(false);
  // Synchronous mirror of ``isStreaming`` — the one-request-at-a-time
  // gate. React state updates are async, so two chip clicks in the
  // same frame both see stale ``isStreaming``; the ref flips before
  // any await and closes that race.
  const inFlightRef = useRef(false);
  // AbortController for the in-flight ``chatWithDocumentStream`` fetch —
  // ``abortChat`` triggers it when the writer clicks Stop. Null between
  // sends; set at the top of ``performSend`` and cleared in its finally.
  const abortRef = useRef<AbortController | null>(null);
  const [liveTool, setLiveTool] = useState<LiveTool | null>(null);
  const [lastReply, setLastReply] = useState<string | null>(null);
  // Persisted across sends so multi-turn threading works end-to-end
  // without the shell having to own this piece of state.
  const [conversationId, setConversationId] = useState<string | undefined>();

  // Seed the conversation ID from the restored session once it arrives.
  // useRef guard ensures we apply it exactly once and never override a
  // live conversation ID that was set by an in-progress chat round.
  const convIdSeededRef = useRef(false);
  useEffect(() => {
    if (!convIdSeededRef.current && params.initialConversationId) {
      convIdSeededRef.current = true;
      setConversationId(params.initialConversationId);
    }
  }, [params.initialConversationId]);
  // Queue-and-retry: when the writer submits before the WS bridge
  // has connected, hold the message in this ref and replay it once
  // ``connectionId`` resolves. ``queuedMessage`` mirrors the held
  // text into render state so the rail can show a soft "sending
  // once connection is ready…" hint.
  const pendingRef = useRef<{
    text: string;
    scope: ResolvedScope;
    target: ChatDispatchTarget;
  } | null>(null);
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);
  // v3 agent state — empty/idle when the v3 path isn't engaged. We
  // keep the fields on the hook regardless of ``useAgentV3`` so
  // consumers (rail / shell) can subscribe unconditionally; the
  // values simply stay at their idle defaults when v3 isn't in use.
  const [tasks, setTasks] = useState<ChatTask[]>([]);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [currentTaskTitle, setCurrentTaskTitle] = useState<string | null>(null);
  const [draftingStatus, setDraftingStatus] = useState<string | null>(null);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [streamingDraft, setStreamingDraft] = useState<string | null>(null);
  const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null);
  const [streamingIntent, setStreamingIntent] = useState<import("../../api/client").ChatRoutedIntent | null>(null);
  // Live tool steps — ref + mirrored state (see LiveStepsStore).
  const liveStepsRef = useRef<readonly LiveToolStep[]>([]);
  const [liveSteps, setLiveStepsState] = useState<readonly LiveToolStep[]>([]);
  const liveStepsStore = useMemo<LiveStepsStore>(
    () => ({
      get: () => liveStepsRef.current,
      set: (steps) => {
        liveStepsRef.current = steps;
        setLiveStepsState(steps);
      },
    }),
    [],
  );

  // The actual fetch path — reused by both the immediate-send and
  // the queued-replay branches so the streaming wiring lives in one
  // place. Returns null on success, error string on failure
  // (matches the public ``sendChat`` contract).
  const performSend = useCallback(
    async (text: string, _scope: ResolvedScope, target: ChatDispatchTarget): Promise<string | null> => {
      // One request at a time — every entry point (chip click, typed
      // input, quoted reply, queued replay) funnels through here, so
      // this single check prevents simultaneous agent requests.
      if (inFlightRef.current) return CHAT_BUSY_MESSAGE;
      inFlightRef.current = true;
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      setLiveTool(null);
      setStatusLabel(null);
      setStreamingDraft(null);
      setStreamStartedAt(Date.now());
      setStreamingIntent(target.intent ?? null);
      liveStepsStore.set([]);
      // Reset v3 surfaces at the start of every send so a fresh
      // plan/progress sequence renders cleanly. Idempotent when the
      // v3 path isn't in use (state was already at defaults).
      setTasks([]);
      setCompletedTasks(0);
      setTotalTasks(0);
      setCurrentTaskId(null);
      setCurrentTaskTitle(null);
      setDraftingStatus(null);

      // Spec 29 — emit the writer's message as a ``prompt`` event
      // BEFORE the fetch so the Timeline reflects the chat in real
      // time. The paired ``askwhy`` lands on ``complete`` (see the
      // DECISION header for why we emit both now).
      params.onEvent({
        ts: Date.now(),
        actor: "you",
        sectionId: target.sectionId,
        // Phase 9 — bridge already knows the writer-facing
        // ``section_number``; format the label here so the entry
        // persists with the writer's vocabulary, not the backend
        // module id.
        sectionLabel: formatSectionLabelFromNumber(target.sectionNumber),
        runId: params.runId,
        payload: {
          kind: "prompt",
          text,
          selectionContext: target.selectedText || undefined,
          quotedMessage: target.quotedMessage,
          intent: target.intent,
          attachments: target.attachments,
        },
      });

      try {
        await chatWithDocumentStream(
          {
            message: text,
            // Empty string when no post-run docx is resolved — the
            // backend treats ``docx_path == ""`` as "use live bridge"
            // (server/routes/chat.py L360: ``if body.docx_path else
            // None``). The Pydantic schema requires a string, so we
            // send "" rather than omitting.
            docx_path: params.docxPath ?? "",
            section_number: target.sectionNumber,
            conversation_id: conversationId,
            // connection_id unlocks the approval bridge on the
            // backend — without it, tools that need writer sign-off
            // (search_literature, request_user_input) fall into
            // non-interactive reject-by-default mode.
            connection_id: params.connectionId ?? undefined,
            library_id: params.libraryId,
            trial: params.trial,
            session: params.session,
            // Scopes marker-removal attribution to the section-issue ledger.
            run_id: params.runId ?? undefined,
            doc_type: params.docType,
            selected_text: target.selectedText || undefined,
            quoted_message: target.quotedMessage as Record<string, unknown> | undefined,
            // Persisted onto the server-side user display message so
            // restored history keeps the intent chip.
            intent: target.intent,
            mode: target.mode ?? "default",
            use_agent_v3: params.useAgentV3 ?? false,
            library_mode: params.libraryMode,
            attachments: target.attachments,
          },
          (event) =>
            handleStreamEvent({
              event,
              question: text,
              intent: target.intent,
              sectionId: target.sectionId,
              sectionLabel: formatSectionLabelFromNumber(target.sectionNumber),
              runId: params.runId,
              setLiveTool,
              setLastReply,
              setConversationId,
              setTasks,
              setCompletedTasks,
              setTotalTasks,
              setCurrentTaskId,
              setCurrentTaskTitle,
              setDraftingStatus,
              setStatusLabel,
              setStreamingDraft,
              liveSteps: liveStepsStore,
              onEvent: params.onEvent,
            }),
          controller.signal,
        );
        return null;
      } catch (err) {
        // Writer clicked Stop — a user-initiated abort is not an error,
        // so swallow it (no "Chat request failed…" banner). The finally
        // resets streaming state, reverting the composer to Send.
        if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
          return null;
        }
        const detail = err instanceof Error ? err.message : String(err);
        return `Chat request failed: ${detail}`;
      } finally {
        abortRef.current = null;
        inFlightRef.current = false;
        setIsStreaming(false);
        setLiveTool(null);
        setStatusLabel(null);
        setStreamingDraft(null);
        setStreamStartedAt(null);
        setStreamingIntent(null);
        // complete/error already flushed unresolved steps to the log;
        // this only clears leftovers after a transport-level failure.
        liveStepsStore.set([]);
      }
    },
    [
      params.docxPath,
      params.libraryId,
      params.trial,
      params.session,
      params.docType,
      params.runId,
      params.connectionId,
      params.useAgentV3,
      params.libraryMode,
      params.onEvent,
      conversationId,
      liveStepsStore,
    ],
  );

  const sendChat = useCallback(
    async (message: string, scope: ResolvedScope, target: ChatDispatchTarget): Promise<string | null> => {
      const text = message.trim();
      if (text.length === 0) return null;

      // Fail fast with a writer-readable message rather than silently
      // swallow the send. Validates at the boundary (principle #3).
      // See ``checkChatPreconditions`` for the gate logic + tests.
      const preconditionError = checkChatPreconditions(params.docxPath, params.connectionId);
      if (preconditionError) {
        // Bridge isn't ready. Distinguish "still dialing" from
        // "definitively unreachable" so the writer sees the right
        // signal: queue the send across the connect transition, or
        // surface a clear error pointing at the actual problem
        // (server unreachable). Defaults to ``connected`` for callers
        // that haven't threaded the status through yet — preserves
        // the legacy proceed-or-error contract.
        const status = params.bridgeStatus ?? "connected";
        const block = classifyBridgeBlock(status);
        if (block.kind === "queue") {
          pendingRef.current = { text, scope, target };
          setQueuedMessage(text);
          // Returning null = no errorHint in the rail. The rail
          // reads ``queuedMessage`` separately to show the soft
          // "queued" indicator.
          return null;
        }
        return block.message;
      }

      return performSend(text, scope, target);
    },
    [params.docxPath, params.connectionId, params.bridgeStatus, performSend],
  );

  // Replay any queued message once the bridge connects. Runs only
  // on the null → string transition of ``connectionId``; subsequent
  // re-renders without a queued message are no-ops.
  useEffect(() => {
    if (!params.connectionId) return;
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    setQueuedMessage(null);
    void performSend(pending.text, pending.scope, pending.target);
  }, [params.connectionId, performSend]);

  // Drop the queued message if the bridge gives up entirely. Without
  // this, a queued message would silently linger across a
  // connect-failure → manual-retry cycle and fire on a future
  // reconnect the writer no longer expects.
  useEffect(() => {
    if (params.bridgeStatus === "disconnected" && pendingRef.current) {
      pendingRef.current = null;
      setQueuedMessage(null);
    }
  }, [params.bridgeStatus]);

  const clearLastReply = useCallback(() => setLastReply(null), []);

  // Stop the in-flight request. No-op when idle (abortRef is null
  // between sends). Aborting rejects the fetch read loop, whose
  // finally clears ``isStreaming`` and the rest of the stream state.
  const abortChat = useCallback(() => abortRef.current?.abort(), []);

  // ``/abbreviations`` — a command-triggered stream that reuses the chat
  // tool-step machinery. It shares the one-request-at-a-time gate,
  // streaming-state setup/teardown, and ``handleStreamEvent`` pump with
  // ``performSend``; it does NOT emit a ``prompt`` event (the command
  // dispatcher already logged ``you: /abbreviations``) and takes no
  // conversation/bridge params — the server reads the run's SSOT directly.
  const runAbbreviations = useCallback(
    async (runId: string): Promise<string | null> => {
      if (inFlightRef.current) return CHAT_BUSY_MESSAGE;
      inFlightRef.current = true;
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      setLiveTool(null);
      setStatusLabel(null);
      setStreamingDraft(null);
      setStreamStartedAt(Date.now());
      setStreamingIntent(null);
      liveStepsStore.set([]);
      setTasks([]);
      setCompletedTasks(0);
      setTotalTasks(0);
      setCurrentTaskId(null);
      setCurrentTaskTitle(null);
      setDraftingStatus(null);

      try {
        await streamAbbreviations(
          runId,
          (event) =>
            handleStreamEvent({
              event,
              question: "/abbreviations",
              sectionId: null,
              sectionLabel: null,
              runId,
              setLiveTool,
              setLastReply,
              setConversationId,
              setTasks,
              setCompletedTasks,
              setTotalTasks,
              setCurrentTaskId,
              setCurrentTaskTitle,
              setDraftingStatus,
              setStatusLabel,
              setStreamingDraft,
              liveSteps: liveStepsStore,
              onEvent: params.onEvent,
            }),
          controller.signal,
        );
        return null;
      } catch (err) {
        if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
          return null;
        }
        const detail = err instanceof Error ? err.message : String(err);
        return `Abbreviations build failed: ${detail}`;
      } finally {
        abortRef.current = null;
        inFlightRef.current = false;
        setIsStreaming(false);
        setLiveTool(null);
        setStatusLabel(null);
        setStreamingDraft(null);
        setStreamStartedAt(null);
        setStreamingIntent(null);
        liveStepsStore.set([]);
      }
    },
    [params.onEvent, liveStepsStore],
  );

  return {
    isStreaming,
    liveTool,
    liveSteps,
    lastReply,
    queuedMessage,
    tasks,
    completedTasks,
    totalTasks,
    currentTaskId,
    currentTaskTitle,
    draftingStatus,
    statusLabel,
    streamingDraft,
    streamStartedAt,
    streamingIntent,
    sendChat,
    clearLastReply,
    abortChat,
    runAbbreviations,
  };
}

// ---------------------------------------------------------------------------
// Stream-event handler — extracted so it's testable in isolation
// ---------------------------------------------------------------------------

/** Parameters for the SSE event handler. Extracted to a named type
 *  so a future refactor can unit-test the branching without a
 *  hook. Each setter is the React dispatcher bound in the hook. */
export interface StreamEventHandlerParams {
  event: Record<string, unknown>;
  question: string;
  /** Intent the message was routed under — stamped onto the ``askwhy``
   *  entry on ``complete`` so the assistant bubble keeps its chip
   *  after the turn (and across reloads). */
  intent?: import("../../api/client").ChatRoutedIntent;
  sectionId: string | null;
  /** Phase 9 — pre-resolved writer-facing section label. Threaded
   *  alongside ``sectionId`` so emitted EventLog entries carry the
   *  writer's vocabulary (e.g. ``§9.2``) rather than the backend
   *  module id. Null when the request is run-wide. */
  sectionLabel: string | null;
  runId: string | null;
  setLiveTool: (v: LiveTool | null) => void;
  setLastReply: (v: string | null) => void;
  setConversationId: (v: string | undefined) => void;
  /** v3 setters — only fire on v3 SSE events; left untouched for the
   *  legacy chat path. Threaded explicitly (no closures over hook
   *  state) so the handler stays unit-testable. */
  setTasks: (updater: (prev: ChatTask[]) => ChatTask[]) => void;
  setCompletedTasks: (n: number) => void;
  setTotalTasks: (n: number) => void;
  setCurrentTaskId: (v: string | null) => void;
  setCurrentTaskTitle: (v: string | null) => void;
  setDraftingStatus: (v: string | null) => void;
  /** Setup/progress label from ``status`` events. */
  setStatusLabel: (v: string | null) => void;
  /** Mid-stream assistant text from ``text`` events. Updater form so
   *  token deltas append without closing over state. */
  setStreamingDraft: (updater: (prev: string | null) => string | null) => void;
  /** Live tool-step store — running boxes are transient here; resolved
   *  steps are appended to the EventLog as ``chat_tool_step``. */
  liveSteps: LiveStepsStore;
  onEvent: (entry: AppendInput) => void;
}

/**
 * Per-SSE-event branch. Pure in the sense that every effect goes
 * through an explicit callback — no global state, no closures over
 * ambient React state. This is what makes the handler testable.
 *
 * Exported for tests + future callers; the hook is the only
 * production caller today.
 */
export function handleStreamEvent(args: StreamEventHandlerParams): void {
  const {
    event,
    question,
    intent,
    sectionId,
    sectionLabel,
    runId,
    setLiveTool,
    setLastReply,
    setConversationId,
    setTasks,
    setCompletedTasks,
    setTotalTasks,
    setCurrentTaskId,
    setCurrentTaskTitle,
    setDraftingStatus,
    setStatusLabel,
    setStreamingDraft,
    liveSteps,
    onEvent,
  } = args;

  // Append the durable ``chat_tool_step`` entry for a resolved (or
  // flushed) step and drop it from the transient store. Durable-first
  // ordering so the box never disappears for a frame.
  const resolveStep = (
    step: LiveToolStep,
    status: "done" | "error",
    resultPreview?: string,
    result?: { summary?: string; detail?: string },
  ): void => {
    onEvent({
      // ``ts`` = start time so steps sort before the final answer;
      // startedAt is already monotonic-guarded per step (see below).
      ts: step.startedAt,
      actor: "asthra",
      sectionId,
      sectionLabel,
      runId,
      payload: {
        kind: "chat_tool_step",
        tool: step.tool,
        stepId: step.stepId,
        status,
        inputSummary: step.summaryOverride ?? describeToolCall(step.tool, step.input ?? {}, true),
        resultPreview: resultPreview ? resultPreview.slice(0, 200) : undefined,
        ...(step.toolQuery ? { toolQuery: step.toolQuery } : {}),
        resultSummary: result?.summary || undefined,
        resultDetail: result?.detail || undefined,
        durationMs: Math.max(0, Date.now() - step.startedAt),
      },
    });
    liveSteps.set(liveSteps.get().filter((s) => s.stepId !== step.stepId));
  };

  // Push a transient running step. ``startedAt`` is nudged past the
  // previous step's so the durable entries' merge keys can never
  // collide within a turn (tools resolving in the same millisecond).
  const pushStep = (
    tool: string,
    input?: Record<string, unknown>,
    opts?: { stepId?: string; summaryOverride?: string; toolQuery?: string },
  ): void => {
    const steps = liveSteps.get();
    const last = steps[steps.length - 1];
    const startedAt = Math.max(Date.now(), (last?.startedAt ?? 0) + 1);
    liveSteps.set([
      ...steps,
      {
        stepId: opts?.stepId ?? `${tool}:${startedAt}:${steps.length}`,
        tool,
        input,
        startedAt,
        summaryOverride: opts?.summaryOverride,
        ...(opts?.toolQuery ? { toolQuery: opts.toolQuery } : {}),
      },
    ]);
  };

  // Flush every still-running step as an error row — called on
  // ``complete``/``error`` so history is never silently lossy about a
  // step that started but never reported back.
  const flushUnresolved = (): void => {
    for (const step of [...liveSteps.get()]) resolveStep(step, "error");
  };

  const eventType = typeof event.type === "string" ? event.type : "";

  // ----- setup / round progress -----
  if (eventType === "status") {
    // Backend setup-phase marker (accepted → doc_context →
    // health_check → history → thinking). Feeds the rail's status
    // line during the window that used to render a static
    // "Thinking…" for 5-15s.
    const label = typeof event.label === "string" ? event.label : null;
    setStatusLabel(label);
    return;
  }

  if (eventType === "text") {
    // Mid-stream assistant text. Both chat routes stream token deltas
    // (append). Clear the tool/status line — the model is talking now.
    const t = typeof event.text === "string" ? event.text : "";
    if (t.length > 0) {
      setStreamingDraft((prev) => (prev ?? "") + t);
      setLiveTool(null);
      setStatusLabel(null);
    }
    return;
  }

  // ----- v3 planner / progress events -----
  if (eventType === "plan_ready") {
    const rawTasks = Array.isArray(event.tasks) ? event.tasks : [];
    const parsed: ChatTask[] = rawTasks.map((t) => parseChatTask(t)).filter((t): t is ChatTask => t !== null);
    setTasks(() => parsed);
    setTotalTasks(parsed.length);
    setCompletedTasks(0);
    setCurrentTaskId(null);
    setCurrentTaskTitle(null);
    setDraftingStatus(null);
    return;
  }

  if (eventType === "task_start") {
    const taskId = typeof event.task_id === "string" ? event.task_id : null;
    const title = typeof event.title === "string" ? event.title : null;
    setCurrentTaskId(taskId);
    setCurrentTaskTitle(title);
    if (taskId !== null) {
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: "in_progress" } : t)));
    }
    // v3 planner task → activity box (tool id "task"; the title is the
    // writer-facing summary).
    pushStep("task", undefined, { stepId: taskId ?? undefined, summaryOverride: title ?? undefined });
    return;
  }

  if (eventType === "task_done" || eventType === "task_failed" || eventType === "task_skipped") {
    const taskId = typeof event.task_id === "string" ? event.task_id : null;
    const failureReason = typeof event.failure_reason === "string" ? event.failure_reason : null;
    const nextStatus: ChatTask["status"] =
      eventType === "task_done" ? "done" : eventType === "task_failed" ? "failed" : "skipped";
    if (taskId !== null) {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? {
                ...t,
                status: nextStatus,
                failure_reason: failureReason ?? t.failure_reason ?? null,
              }
            : t,
        ),
      );
    }
    setCurrentTaskId(null);
    setCurrentTaskTitle(null);
    // Resolve the matching activity box (by task id, else oldest task
    // step — the v3 loop runs tasks sequentially).
    {
      const steps = liveSteps.get();
      const step = (taskId !== null && steps.find((s) => s.stepId === taskId)) || steps.find((s) => s.tool === "task");
      if (step) {
        const status = eventType === "task_failed" ? "error" : "done";
        const preview = eventType === "task_skipped" ? "skipped" : (failureReason ?? undefined);
        resolveStep(step, status, preview);
      }
    }
    return;
  }

  if (eventType === "task_progress") {
    // Cumulative counter from the backend — single source of truth.
    // We trust the server's count rather than incrementing locally so
    // skipped/failed tasks land in the same bucket without bridge
    // logic having to mirror the server's task-resolution rules.
    const completed = typeof event.completed === "number" ? event.completed : null;
    const total = typeof event.total === "number" ? event.total : null;
    if (completed !== null) setCompletedTasks(completed);
    if (total !== null) setTotalTasks(total);
    return;
  }

  if (eventType === "drafting_response") {
    // The planner has resolved every task and Sonnet is composing
    // the final reply. The rail surfaces this as a "Drafting…" line
    // so the writer knows the work isn't stalled — final text
    // arrives via ``complete``.
    const status = typeof event.status === "string" ? event.status : "Drafting final response…";
    setDraftingStatus(status);
    setCurrentTaskId(null);
    setCurrentTaskTitle(null);
    // Retrieval is done; clear the per-tool line so it doesn't show a
    // stale "searching…" verb while Sonnet composes the reply.
    setLiveTool(null);
    return;
  }

  if (eventType === "retrieval_step") {
    // Retrieval-cascade visibility (v3 path) — emitted per cascade
    // step. The backend sends ``tool`` + ``surface`` + ``hint``
    // (server/routes/chat_agent_v3.py:594/615), so route those
    // through the same SSOT the legacy ``tool_call_start`` path uses
    // to keep the status copy consistent across both chat paths.
    const tool = typeof event.tool === "string" ? event.tool : null;
    const surface = typeof event.surface === "string" ? event.surface : undefined;
    const hint = typeof event.hint === "string" ? event.hint : undefined;
    if (tool) {
      setLiveTool({
        name: tool,
        input: hint ? { query: hint } : undefined,
        surface,
      });
    }
    return;
  }

  if (eventType === "tool_call_start") {
    // Legacy path. Carry the input alongside the name so the rail
    // can show context ("searching PubMed for …"), not just a verb.
    const tool = typeof event.tool === "string" ? event.tool : null;
    // The SDK agent sends the flattened ``tool_query``, not the input dict:
    // re-key it under the tool's context param so the row still says what
    // the step looked for.
    const input =
      event.input && typeof event.input === "object"
        ? (event.input as Record<string, unknown>)
        : tool && typeof event.tool_query === "string"
          ? inputFromQuery(tool, event.tool_query)
          : undefined;
    setLiveTool(tool ? { name: tool, input } : null);
    // The tool verb supersedes any setup/thinking label.
    setStatusLabel(null);
    // The untruncated main argument, for actions keyed on it (the chat's
    // "Open impacted sections" reads find_impacted_sections' document).
    const rawDoc =
      event.input && typeof event.input === "object" ? (event.input as Record<string, unknown>).document : undefined;
    const toolQuery =
      typeof rawDoc === "string" && rawDoc.trim()
        ? rawDoc.trim()
        : typeof event.tool_query === "string" && event.tool_query.trim()
          ? event.tool_query.trim()
          : undefined;
    if (tool) pushStep(tool, input, toolQuery ? { toolQuery } : undefined);
    return;
  }

  if (eventType === "tool_call_result") {
    // Tool finished. Clear the status line until the next tool
    // starts (or the overall ``complete`` fires).
    setLiveTool(null);
    // Resolve the oldest matching running step into a durable
    // ``chat_tool_step`` entry (the loop executes tools sequentially,
    // so FIFO-by-tool-name is exact).
    const resultTool = typeof event.tool === "string" ? event.tool : null;
    const preview = typeof event.result_preview === "string" ? event.result_preview : undefined;
    const result = {
      summary: typeof event.result_summary === "string" ? event.result_summary : undefined,
      detail: typeof event.result_detail === "string" ? event.result_detail : undefined,
    };
    const steps = liveSteps.get();
    const step = (resultTool && steps.find((s) => s.tool === resultTool)) || steps[0];
    if (step) resolveStep(step, event.ok === false ? "error" : "done", preview, result);
    return;
  }

  if (eventType === "complete") {
    // An empty message would leave the turn with no assistant bubble at
    // all (messageProjection drops askwhy entries with an empty answer)
    // — a dead-end after the tool activity boxes. The server guards
    // against this too; this is the belt-and-braces client half.
    const rawAnswer = typeof event.message === "string" ? event.message : "";
    const answer = rawAnswer.trim() ? rawAnswer : EMPTY_REPLY_FALLBACK;
    const nextConvoId = typeof event.conversation_id === "string" ? event.conversation_id : undefined;
    const changes = parseChatChanges(event.changes);
    if (nextConvoId !== undefined) setConversationId(nextConvoId);
    flushUnresolved();
    setLastReply(answer);
    setLiveTool(null);
    setStatusLabel(null);
    setStreamingDraft(() => null);
    setDraftingStatus(null);
    setCurrentTaskId(null);
    setCurrentTaskTitle(null);

    // Audit-grade record. One event per Q&A round; the Timeline
    // view renders this with the question + answer pair.
    onEvent({
      ts: Date.now(),
      actor: "asthra",
      sectionId,
      sectionLabel,
      runId,
      payload: {
        kind: "askwhy",
        question,
        answer,
        intent,
        ...(changes.length > 0 ? { changes } : {}),
      },
    });
    return;
  }

  if (eventType === "error") {
    const errorText = typeof event.error === "string" ? event.error : "chat failed";
    flushUnresolved();
    setDraftingStatus(null);
    setStatusLabel(null);
    setStreamingDraft(() => null);
    setCurrentTaskId(null);
    setCurrentTaskTitle(null);
    // # DECISION (2026-04-24): error events emit a timeline row in
    // #   addition to setting lastReply. Without the timeline row,
    // #   errors were invisible in idle state — the rail's
    // #   ``assistantMessage`` is derived from reducer state and
    // #   returns null for idle, so ``lastReply`` alone never
    // #   rendered. This cost a writer 30 min of "why is nothing
    // #   happening?" debugging. Emit both paths — timeline row
    // #   for durable audit, lastReply for inline feedback.
    setLastReply(`Error: ${errorText}`);
    onEvent({
      ts: Date.now(),
      actor: "asthra",
      sectionId,
      sectionLabel,
      runId,
      payload: { kind: "askwhy", question, answer: `Error: ${errorText}` },
    });
    return;
  }

  // Unknown event types are ignored (forward-compatible with
  // future backend extensions that add new event types the bridge
  // doesn't yet understand).
}

// ---------------------------------------------------------------------------
// v3 helpers
// ---------------------------------------------------------------------------

/** Parse a ``ChatTask`` payload from the SSE wire format. Returns
 *  null for malformed entries (id/title/intent missing) so callers
 *  can ``filter(Boolean)`` over the planner's task array without
 *  letting bad data crash the rail. */
function parseChatTask(raw: unknown): ChatTask | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const id = typeof obj.id === "string" ? obj.id : null;
  const title = typeof obj.title === "string" ? obj.title : null;
  const intent = typeof obj.intent === "string" ? obj.intent : null;
  if (!id || !title || !intent) return null;
  const status = typeof obj.status === "string" ? obj.status : "pending";
  const failureReason = typeof obj.failure_reason === "string" ? obj.failure_reason : null;
  return {
    id,
    title,
    intent,
    status: normaliseTaskStatus(status),
    failure_reason: failureReason,
  };
}

function normaliseTaskStatus(s: string): ChatTask["status"] {
  if (s === "in_progress" || s === "done" || s === "failed" || s === "skipped") {
    return s;
  }
  return "pending";
}

// ---------------------------------------------------------------------------
// Copy helpers
// ---------------------------------------------------------------------------

/**
 * Build the writer-facing streaming status line shown above the rail
 * input. Pure helper — keeps the copy in one place so the rail
 * component stays a thin renderer.
 *
 * Delegates to ``buildLiveStatusPhrase`` (the tool-display SSOT) so the
 * verb is grammatical ("searching", not "search") and carries context
 * ("searching PubMed for …"). The same SSOT drives the completed
 * tool-call trace, so the two never drift apart.
 *
 * @param liveTool — the tool currently running per the SSE stream, or
 *   null when no tool is active (between tools, or during the initial
 *   think-time before the first tool fires).
 * @param intentLabelText — human label for the classified intent of
 *   the in-flight message (e.g. "Edit highlight"). When present the
 *   returned line is prefixed "[Label] …" so the intent stays visible
 *   through the whole streaming window. Null/omitted → no prefix.
 */
export function buildStreamingStatus(
  liveTool: LiveTool | null,
  statusLabel?: string | null,
  intentLabelText?: string | null,
): string {
  const prefix = intentLabelText ? `[${intentLabelText}] ` : "";
  if (liveTool) {
    const phrase = buildLiveStatusPhrase(liveTool.name, liveTool.input, liveTool.surface);
    return `${prefix}Agent is ${phrase}…`;
  }
  // No tool running — surface the backend's setup/round label
  // (health check, history load, "Thinking…") when it has one.
  if (statusLabel) return `${prefix}${statusLabel}`;
  return `${prefix}Agent is thinking…`;
}
